import type { Action, Computed, ThunkOn } from 'easy-peasy';
import { action, computed, thunkOn } from 'easy-peasy';
import type { StoreModel } from '..';
import objecthash from 'object-hash';
export type FetchKey =
  | 'fetchFulfillmentCenters'
  | 'fetchCarrierAccounts'
  | 'getPartnerOptIns'
  | 'getOptInsList'
  | 'fetchCarrierAccountTestLabels'
  | 'fetchCarrierAccountLabelAugmentations'
  | 'fetchValidLabelConfigurationsByFC'
  | 'fetchValidLabelConfigurations'
  | 'fetchReturnsLabelAugmentations'
  | 'fetchShipmentById'
  | 'getPartnerApiKeys'
  | 'getPartnerOauthClients'
  | 'fetchUsers'
  | 'fetchDeliveryEstimateById'
  | 'fetchPartner'
  | 'fetchPartnerLimits'
  | 'fetchJobs'
  | 'fetchFulfillmentContextLinkProperties'
  | 'fetchSampleCarrierSelectionByCacheId'
  | 'fetchCarrierRateSets'
  | 'fetchTenants'
  | 'fetchPCOrigins'
  | 'fetchPCOrigin'
  | 'fetchPCSchedules'
  | 'fetchPCTenants'
  | 'fetchPCTenant'
  | 'fetchPCWebhooks'
  | 'fetchPCWebhook'
  | 'updatePCWebhook'
  | 'createPCWebhook'
  | 'sendTestEvent'
  | 'fetchOrigins'
  | 'getPartnerOptIns';

export interface DataFetchingModel {
  fetchedData: Record<string, number>;
  onDataFetch: ThunkOn<DataFetchingModel, void, StoreModel>;
  checkFetched: Computed<DataFetchingModel, (fetchKey: FetchKey, params?: any, timeOutDuration?: number) => boolean>;
  isFetchComplete: Computed<DataFetchingModel, (fetchKey: FetchKey, params?: any, timeOutDuration?: number) => boolean>;
  setFetchedData: Action<DataFetchingModel, { key: string; params: any }>;
  expireFetchedData: Action<DataFetchingModel, { key?: FetchKey; params?: any } | undefined>;
  fetchingData: Record<string, boolean>;
  setFetchingData: Action<DataFetchingModel, { key: string; params: any }>;
  removeFetchingData: Action<DataFetchingModel, { key: string; params: any }>;
  onDataFetchStart: ThunkOn<DataFetchingModel, void, StoreModel>;
}
// Resolved by thunks that intentionally skip their fetch (e.g. missing authorities).
// Identity-checked in onDataFetch so a skipped fetch is never recorded as fetched,
// which would otherwise block a real fetch later in the same session.
export const SKIPPED_FETCH: never[] = [];

export const createFetchedHash = (fetchKey: string, params: any) => {
  if (params) {
    return `${fetchKey}_${objecthash(params)}`;
  } else {
    return fetchKey;
  }
};
export const dataFetchingModel: DataFetchingModel = {
  fetchedData: {},
  fetchingData: {},
  checkFetched: computed((state) => {
    return (fetchKey: FetchKey, params?: any, timeOutDuration?: number) => {
      const key = createFetchedHash(fetchKey, params);
      return (
        state.fetchingData[key] ||
        (!!state.fetchedData[key] && (!timeOutDuration || state.fetchedData[key] + timeOutDuration > Date.now()))
      );
    };
  }),
  isFetchComplete: computed((state) => {
    return (fetchKey: FetchKey, params?: any, timeOutDuration = 2000) => {
      const key = createFetchedHash(fetchKey, params);
      return (
        state.fetchingData[key] === false &&
        !!state.fetchedData[key] &&
        state.fetchedData[key] + timeOutDuration > Date.now()
      );
    };
  }),
  setFetchedData: action((state, payload) => {
    const { key, params } = payload;
    state.fetchedData = { ...state.fetchedData, [createFetchedHash(key, params)]: Date.now() };
  }),
  setFetchingData: action((state, payload) => {
    const { key, params } = payload;
    state.fetchingData = { ...state.fetchingData, [createFetchedHash(key, params)]: true };
  }),
  removeFetchingData: action((state, payload) => {
    const { key, params } = payload;
    state.fetchingData = { ...state.fetchingData, [createFetchedHash(key, params)]: false };
  }),
  // allows for expiring all fetched data,
  // all fetched data for a given fetch call,
  // & fetched data for a give call + params
  expireFetchedData: action((state, payload) => {
    const modifiedFetched = { ...state.fetchedData };
    if (payload) {
      const { key, params } = payload;
      if (key && params) {
        delete modifiedFetched[createFetchedHash(key, params)];
        state.fetchedData = modifiedFetched;
      } else if (key) {
        // this is slow but there shouldn't ever be that many keys
        const newFetched = { ...state.fetchedData };
        const expiredKeys = Object.keys(newFetched).filter((k) => k.split('_')[0] === key);
        expiredKeys.forEach((k) => {
          delete newFetched[k];
        });
        state.fetchedData = modifiedFetched;
      }
    } else {
      state.fetchedData = {};
    }
  }),
  onDataFetchStart: thunkOn(
    (actions, storeActions) => [
      storeActions.fulfillmentCenterConfig.fetchFulfillmentCenters.startType,
      storeActions.carrier.fetchCarrierList.startType,
      storeActions.shipments.fetchShipmentById.startType,
      storeActions.security.getPartnerApiKeys.startType,
      storeActions.security.getPartnerOauthClients.startType,
      storeActions.users.fetchUsers.startType,
      storeActions.shipments.fetchDeliveryEstimateById.startType,
      storeActions.partner.fetchPartner.startType,
      storeActions.fulfillmentContextLinkProperties.fetchFulfillmentContextLinkProperties.startType,
      storeActions.tenant.fetchTenants.startType,
      storeActions.partnerConfiguration.origins.fetchPCOrigins.startType,
      storeActions.partnerConfiguration.origins.fetchPCOrigin.startType,
      storeActions.partnerConfiguration.origins.fetchPCSchedules.startType,
      storeActions.partnerConfiguration.tenants.fetchPCTenants.startType,
      storeActions.partnerConfiguration.tenants.fetchPCTenant.startType,
      storeActions.origins.fetchOrigins.startType,
    ],
    (actions, payload) => {
      const payloadType = payload.type.split('.').pop();
      if (payloadType) {
        actions.setFetchingData({ key: payloadType.slice(0, payloadType.indexOf('(')), params: payload.payload });
      }
    },
  ),
  onDataFetch: thunkOn(
    (actions, storeActions) => [
      storeActions.fulfillmentCenterConfig.fetchFulfillmentCenters,
      storeActions.carrier.fetchCarrierList,
      storeActions.shipments.fetchShipmentById,
      storeActions.security.getPartnerApiKeys,
      storeActions.security.getPartnerOauthClients,
      storeActions.users.fetchUsers,
      storeActions.shipments.fetchDeliveryEstimateById,
      storeActions.partner.fetchPartner,
      storeActions.fulfillmentContextLinkProperties.fetchFulfillmentContextLinkProperties,
      storeActions.tenant.fetchTenants,
      storeActions.partnerConfiguration.origins.fetchPCOrigins,
      storeActions.partnerConfiguration.origins.fetchPCOrigin,
      storeActions.partnerConfiguration.origins.fetchPCSchedules,
      storeActions.partnerConfiguration.tenants.fetchPCTenants,
      storeActions.partnerConfiguration.tenants.fetchPCTenant,
      storeActions.origins.fetchOrigins,
    ],
    (actions, payload) => {
      const payloadType = payload.type.split('.').pop();
      if (payloadType) {
        if (!payload.error && payload.result !== SKIPPED_FETCH) {
          actions.setFetchedData({ key: payloadType.slice(0, payloadType.indexOf('(')), params: payload.payload });
        }
        actions.removeFetchingData({ key: payloadType.slice(0, payloadType.indexOf('(')), params: payload.payload });
      }
    },
  ),
};
